/**
 * Générateur — Automatisme : puissances simples : 2² = 4 ; 2³ = 8 ; 3³ = 27.
 * Attendu ID : 1103 | Niveau : 4ème | Domaine : Puissances
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 677 :
 * « Puissances simples : 2² = 4 ; 2³ = 8 ; 3³ = 27. »).
 * Sert AUSSI l'id 1104 (l. 678 « 10² = 100 ; 10³ = 1 000. ») par le wrapper
 * gen_1104 : le MÊME gen restreint à la base 10 (clés `dix_*`, comme les
 * clés produits_/quotients_ de 1043). Un re-badge nu ferait sortir 2³ sous
 * l'id « 10² = 100 » ; le dispatch par clé l'évite.
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * EXPOSANTS 2 ET 3 SEULEMENT : la seule notation connue avant l'objectif
 * l. 682 (« définir les puissances d'exposant positif ») ; RA16 Puissances
 * p. 2 : « la notation aⁿ peut être introduite avec les exposants 2 et 3
 * comme un raccourci d'écriture exprimant l'aire d'un carré ou le volume
 * d'un cube » ; 5e l. 589 « carré et cube ». 2⁴ = 16 EXCLU (exposant 4 : 3e
 * l. 743), 10⁴ EXCLU (ancien gen 136). « Simples » = carrés et cubes de
 * petits nombres : (2 ; 2), (2 ; 3), (3 ; 2), (3 ; 3), (4 ; 2), (4 ; 3),
 * (5 ; 2), (5 ; 3), (10 ; 2), (10 ; 3) ; 4³ = 64 et 5³ = 125 admis par le
 * prof le 2026-09-12 (banque 4NA6 : cubes de 0 à 5 ; le warning de
 * variabilité du QCM tombe), 6³ rejeté ; (1 ; 3) rare, discriminant (1³ = 1,
 * pas 3). Rien de neuf en
 * calcul (cubes = 5e l. 592) : l'automatisation.
 * Frontières : produits de puissances (l. 683-684), a⁰ (mise en œuvre
 * p. 17), 3⁴ (3e), puissances de 10 à exposant négatif (136-137).
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 4NA6 option 0
 * (carrés 0..10, cubes 0..5 et 10), 4NA5 option 0 (10ⁿ, n 0..10) : bornes
 * trop larges, réduites aux exposants 2 et 3.
 *
 * ── Plan de cartes (1103 ; 1104 = les mêmes en base 10) ────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX,
 *     un entier à saisir : un carré (base 3..5), un cube (2 ou 3), une base
 *     10 (10² ou 10³), une forme inverse à exposant verrouillé (8 = …³ ;
 *     27 = 3^…) ; ni (2 ; 2) ni (1 ; 3) en forme inverse (4 = 2² lu 2 × 2,
 *     1 = 1³ = 1² : le trou ne trancherait rien).
 *   Carte 2 « Combien vaut… ? » (valeur_qcm_unique) : « Combien vaut 2³ ? »,
 *     QUATRE choix : 8 (clé), 6 (base_fois_exposant, LA confusion), 4 ou 16
 *     (compte_faux), 5 ou 23 (base_plus_exposant, concatenation) ; 4 < 6 <
 *     8 < 16, la clé encadrée ; (2 ; 2) exclu (2 × 2 = 2²).
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes mêlant
 *     valeurs et écritures, toujours une ligne « 2³ = 2 × 3 » (F) ; « 2³ = 2
 *     × 2 × 2 » (V) ; « 3² = 6 » (F) ; « 10³ = 1 000 » (V) ; chaque ligne
 *     étiquetée par sa VALEUR (assert), une à trois vraies.
 *   1104 (dix_*) : la Série flash [10² = … ; 10³ = … ; 100 = 10^… ; 10 × 10
 *     × 10 = 10^…], le QCM [1 000 ; 30 ; 100 ; 10 000] ou [100 ; 20 ;
 *     1 000 ; 12], le Vrai/Faux en base 10.
 *
 * INVARIANTS : chaque puissance re-calculée par produit répété (throw) ;
 * base_fois_exposant et l'addition itérée (2 + 2 + 2) ont la même valeur :
 * jamais les deux. SACRIFICE : aucun ; l'exemple de la fiche à venir,
 * 4³ = 64 (le cube en couches de 1059), était hors plage : 4³ étant admis,
 * la fiche prendra 6³ = 216 (hors plage), rien à sacrifier.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1103;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Combien vaut… ?';
const L_VF = 'Vrai ou faux';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les puissances simples [base, exposant] ; (1 ; 3) rare et discriminant. 4³ et 5³ admis par le prof le 2026-09-12 (banque 4NA6 : cubes de 0 à 5). */
export const SIMPLES = [[2, 2], [2, 3], [3, 2], [3, 3], [4, 2], [4, 3], [5, 2], [5, 3], [10, 2], [10, 3]];
export const DIX = [[10, 2], [10, 3]];
const RARE = [1, 3];

/** bᵉ par produit répété (assert : exposant 2 ou 3, base entière ≥ 0). */
const puissance = (b, e) => {
    if (!Number.isInteger(b) || b < 0 || (e !== 2 && e !== 3)) throw new Error(`gen_1103 : hors domaine ${b}^${e}`);
    let p = 1;
    for (let i = 0; i < e; i++) p *= b;
    if (p !== b ** e) throw new Error('gen_1103 : arithmétique');
    return p;
};
const cle = ([b, e]) => `${b}^${e}`;

/** Un couple : parmi `pool`, (1 ; 3) une fois sur huit si `rare`. */
const tirer = (pool, { rare = false, exclure = [] } = {}) => {
    for (let essai = 0; essai < 60; essai++) {
        const c = rare && randInt(1, 8) === 1 ? RARE : randomChoice(pool);
        if (!exclure.some((x) => cle(x) === cle(c))) return c;
    }
    return pool[0];
};

// ─── Écritures ────────────────────────────────────────────────────────────

const puissTex = (b, e) => `${b}^${e}`;
const produitRepete = (b, e) => Array(e).fill(String(b)).join(' \\times ');

const EXPLICATIONS = {
    base_fois_exposant: '2³, c\'est 2 × 2 × 2, pas 2 × 3. Le petit 3 dit combien de fois le 2 est dans le produit.',
    compte_faux: 'Compte les facteurs : 2³, c\'est trois 2 multipliés, ni deux ni quatre.',
    base_plus_exposant: 'Une puissance est un produit, pas une somme : 2³ = 2 × 2 × 2.',
    concatenation: 'Le petit 3 n\'est pas un chiffre du nombre : 2³ se lit « 2 puissance 3 », c\'est 2 × 2 × 2.',
    regle: 'Une puissance est un produit de facteurs tous égaux : 2² = 2 × 2 = 4, 2³ = 2 × 2 × 2 = 8, 10³ = 10 × 10 × 10 = 1 000.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille du gen (catalogue validé ; exposant_lu_comme_chiffre proposé, 2³ → 23 n'est pas la lettre de concatenation). */
const CODE_COMMUN = {
    base_fois_exposant: 'base_fois_exposant',
    base_plus_exposant: 'base_plus_exposant',
    compte_faux: 'nombre_de_facteurs_faux',
    concatenation: 'exposant_lu_comme_chiffre',
};

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre (12,5 = 12.50), son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } (code null : famille rejetée) : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** « bᵉ = … » : b × e, b + e, un facteur de trop ou de moins (comme le QCM), 23 pour 2³ (le QCM), b recopié (l'exposant ignoré). */
const famillesDirecte = (b, e) => {
    const f = [
        { code: 'base_fois_exposant', valeur: b * e }, { code: 'base_plus_exposant', valeur: b + e },
        ...(e === 2 ? [b ** 3] : [b ** 2, b ** 4]).map((valeur) => ({ code: 'nombre_de_facteurs_faux', valeur })),
        { code: CODE_COMMUN.concatenation, valeur: Number(`${b}${e}`) },
    ];
    if (b !== b ** e) f.push({ code: 'exposant_ignore', valeur: b });
    return f;
};

/** Série flash, un trou par ligne retrouvée par son écriture : bᵉ = … ; v = …ᵉ (v ÷ e s'il s'écrit, v − e, e) ; v = b^… (v ÷ b, v − b, e ± 1) ; v recopié est rejeté. */
const erreursFlash = (lignes) => lignes.flatMap((ligne, trou) => {
    const c = [...SIMPLES, RARE].find((x) => [ligneDirecte(x), ligneInverse(x, true), ligneInverse(x, false), ligneProduit(x)].includes(ligne));
    if (!c) return [];
    const [b, e] = c;
    const v = puissance(b, e);
    let nommees;
    if (ligne === ligneDirecte(c)) nommees = nommer(famillesDirecte(b, e), v);
    else if (ligne === ligneInverse(c, true)) {
        nommees = nommer([
            ...((v * 10) % e === 0 ? [{ code: 'base_fois_exposant', valeur: String(v / e).replace('.', ',') }] : []),
            { code: 'base_plus_exposant', valeur: v - e },
            ...(b !== e ? [{ code: 'exposant_au_lieu_de_la_base', valeur: e }] : []),
            { code: null, valeur: v },
        ], b);
    } else {
        nommees = nommer([
            { code: 'base_fois_exposant', valeur: v / b }, { code: 'base_plus_exposant', valeur: v - b },
            { code: 'nombre_de_facteurs_faux', valeur: e - 1 }, { code: 'nombre_de_facteurs_faux', valeur: e + 1 },
            { code: null, valeur: v },
        ], e);
    }
    return nommees.map(({ code, valeur }) => ({ code, trou, valeur }));
});

/** Vrai/faux : chaque ligne fausse sous son code, sauf si la valeur de son membre de droite est aussi une autre erreur (3³ = 3 × 3 = 9). */
const erreursVf = (items) => items.filter((it) => it.code).flatMap((it) => {
    const [b, e] = it.couple;
    const code = CODE_COMMUN[it.code];
    const droite = valeur(it.droite);
    return nommer(famillesDirecte(b, e), puissance(b, e)).some((x) => x.code === code && cleReponse(x.valeur) === cleReponse(droite))
        ? [{ code, ligne: it.ligne }] : [];
});

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b></p>';

const ligneDirecte = ([b, e]) => `$${puissTex(b, e)} = {{${puissance(b, e)}}}$`;
/** Forme inverse à exposant verrouillé : « 8 = …³ » (trou base) ou « 27 = 3^… » (trou exposant). */
const ligneInverse = ([b, e], trouBase) => (trouBase
    ? `$${puissance(b, e)} = {{${b}}}^${e}$`
    : `$${puissance(b, e)} = ${b}^{ {{${e}}} }$`);
const ligneProduit = ([b, e]) => `$${produitRepete(b, e)} = ${b}^{ {{${e}}} }$`;

const assemblerFlash = (lignes, couples, label) => {
    const payload = makeTexteTrou(lignes.join('\n'), CONSIGNE_FLASH, { ...opts(label), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    ranger(payload, erreursFlash(lignes));
    payload._meta.params = { couples };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const carre = tirer([[3, 2], [4, 2], [5, 2]]);
        const cube = tirer([[2, 3], [3, 3], [4, 3], [5, 3]], { rare: true });
        const dix = tirer(DIX);
        const inverse = tirer(SIMPLES, { exclure: [carre, cube, dix, [2, 2]] });
        const couples = [carre, cube, dix, inverse];
        if (new Set(couples.map(cle)).size < 4) continue;
        const lignes = [ligneDirecte(carre), ligneDirecte(cube), ligneDirecte(dix), ligneInverse(inverse, randomChoice([true, false]))];
        return assemblerFlash(lignes, couples, L_FLASH);
    }
    return assemblerFlash([ligneDirecte([3, 2]), ligneDirecte([2, 3]), ligneDirecte([10, 3]), ligneInverse([5, 2], true)], [[3, 2], [2, 3], [10, 3], [5, 2]], L_FLASH);
};

const genFlashDix = () => {
    const [a, b] = shuffle([...DIX]);
    const lignes = [ligneDirecte(a), ligneDirecte(b), ligneInverse(randomChoice(DIX), false), ligneProduit(randomChoice(DIX))];
    return assemblerFlash(lignes, [a, b], L_FLASH);
};

// ─── Carte 2 : QCM « Combien vaut… ? » ────────────────────────────────────

/** Les trois distracteurs d'un couple, valeurs deux à deux distinctes et distinctes de la clé ; null sinon. */
const distracteurs = ([b, e]) => {
    const v = puissance(b, e);
    const compte = e === 2 ? puissance(b, 3) : randomChoice([puissance(b, 2), b ** 4]);
    const cands = [
        { valeur: b * e, code: 'base_fois_exposant' },
        { valeur: compte, code: 'compte_faux' },
        randomChoice([{ valeur: b + e, code: 'base_plus_exposant' }, { valeur: Number(`${b}${e}`), code: 'concatenation' }]),
    ];
    const vals = new Set([v, ...cands.map((c) => c.valeur)]);
    return vals.size === 4 ? cands : null;
};

const assemblerQcm = (couple, faux, label) => {
    const [b, e] = couple;
    const bon = String(puissance(b, e));
    const payload = makeQcm([bon, ...faux.map((x) => String(x.valeur))], bon, `<p><b>Combien vaut ${tex(puissTex(b, e))} ?</b></p>`, opts(label));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    // chaque leurre sous son code, sauf si sa valeur est aussi une autre erreur (3³ → 9, c'est 3 × 3 et 3²)
    const nommees = nommer(famillesDirecte(b, e), bon);
    ranger(payload, faux.map((x) => ({ code: CODE_COMMUN[x.code], valeur: String(x.valeur) }))
        .filter((l) => l.code && nommees.some((n) => n.code === l.code && cleReponse(n.valeur) === cleReponse(l.valeur))));
    payload._meta.params = { b, e, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

const genQcmSur = (pool) => () => {
    for (let essai = 0; essai < 80; essai++) {
        const c = tirer(pool, { exclure: [[2, 2]] });
        const faux = distracteurs(c);
        if (faux) return assemblerQcm(c, faux, L_QCM);
    }
    return assemblerQcm(pool[pool.length - 1], [{ valeur: 30, code: 'base_fois_exposant' }, { valeur: 100, code: 'compte_faux' }, { valeur: 13, code: 'base_plus_exposant' }], L_QCM);
};

// ─── Carte 3 : Vrai ou faux (4 lignes, valeurs et écritures) ──────────────

/** Valeur d'un membre : « 2^3 », « 2 \times 2 \times 2 », « 2 \times 3 », entier. */
const valeur = (s) => {
    let m = s.match(/^(\d+)\^(\d)$/);
    if (m) return puissance(+m[1], +m[2]);
    if (s.includes('\\times')) return s.split(' \\times ').reduce((acc, x) => acc * Number(x), 1);
    return Number(s);
};

const GABARITS = {
    ecriture_fausse: (c) => ({ gauche: puissTex(...c), droite: `${c[0]} \\times ${c[1]}`, vrai: false, code: 'base_fois_exposant' }),
    ecriture_vraie: (c) => ({ gauche: puissTex(...c), droite: produitRepete(...c), vrai: true }),
    valeur_vraie: (c) => ({ gauche: puissTex(...c), droite: String(puissance(...c)), vrai: true }),
    valeur_fausse: (c) => ({ gauche: puissTex(...c), droite: String(c[0] * c[1]), vrai: false, code: 'base_fois_exposant' }),
    compte_faux: (c) => ({ gauche: puissTex(...c), droite: String(puissance(c[0], c[1] === 2 ? 3 : 2)), vrai: false, code: 'compte_faux' }),
    produit_vrai: (c) => ({ gauche: String(puissance(...c)), droite: produitRepete(...c), vrai: true }),
};

const genVfSur = (pool) => () => {
    for (let essai = 0; essai < 120; essai++) {
        const noms = ['ecriture_fausse', ...shuffle(Object.keys(GABARITS).filter((n) => n !== 'ecriture_fausse')).slice(0, 3)];
        const couples = noms.map(() => tirer(pool, { exclure: [[2, 2]] }));
        // quatre couples distincts si le pool le permet (base 10 : deux couples, on tolère les répétitions)
        if (pool.length >= 4 && new Set(couples.map(cle)).size < 4) continue;
        const items = shuffle(noms.map((nom, i) => {
            const g = GABARITS[nom](couples[i]);
            return { nom, couple: couples[i], ...g, ligne: tex(`${g.gauche} = ${g.droite}`) };
        }));
        const nbVraies = items.filter((it) => it.vrai).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        if (new Set(items.map((it) => it.ligne)).size < 4) continue;
        items.forEach((it) => {
            if ((valeur(it.gauche) === valeur(it.droite)) !== it.vrai) throw new Error(`gen_1103 : ligne mal étiquetée ${it.ligne}`);
        });
        const payload = makeVraiFaux(items.map((it) => it.ligne), items.map((it) => (it.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursVf(items));
        payload._meta.params = { items: items.map(({ nom, couple, vrai, code }) => ({ nom, b: couple[0], e: couple[1], vrai, ...(code ? { code } : {}) })) };
        return payload;
    }
    const lignes = [tex('2^3 = 2 \\times 3'), tex('2^3 = 2 \\times 2 \\times 2'), tex('3^2 = 6'), tex('10^3 = 1000')];
    const payload = makeVraiFaux(lignes, [1, 0, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursVf([
        { code: 'base_fois_exposant', couple: [2, 3], droite: '2 \\times 3', ligne: lignes[0] },
        { code: 'base_fois_exposant', couple: [3, 2], droite: '6', ligne: lignes[2] },
    ]));
    payload._meta.params = { items: [{ nom: 'ecriture_fausse', b: 2, e: 3, vrai: false, code: 'base_fois_exposant' }, { nom: 'ecriture_vraie', b: 2, e: 3, vrai: true }, { nom: 'valeur_fausse', b: 3, e: 2, vrai: false, code: 'base_fois_exposant' }, { nom: 'valeur_vraie', b: 10, e: 3, vrai: true }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix distincts ; 2 × 2 = 2² : le couple (2 ; 2) est exclu du QCM ; l\'addition itérée (2 + 2 + 2) a la valeur de base_fois_exposant, jamais les deux',
        'aucun sacrifice : l\'exemple de la fiche à venir, 4³ = 64, est hors plage',
    ],
    qcm_unique: {
        support: '« Combien vaut bᵉ ? », (b ; e) parmi les puissances simples (1103) ou 10² et 10³ (1104)',
        familles: {
            base_fois_exposant: 'b × e : 2³ → 6, 10³ → 30, la confusion principale (gen_926, gen_928)',
            compte_faux: 'un facteur de trop ou de moins : 2³ → 4 ou 16, 10³ → 100 ou 10 000 (nom de 1096)',
            base_plus_exposant: 'b + e : 2³ → 5, 10³ → 13 (liste du doc prof de gen_928)',
            concatenation: 'l\'exposant lu comme un chiffre : 2³ → 23, 10² → 102 (cf. DEPP 4e 2023 q.17, 3x lu 38)',
        },
    },
    vf_tableau_vf: 'quatre lignes, toujours « bᵉ = b × e » (F), puis trois parmi : bᵉ = produit répété (V), valeur juste (V), b × e (F), compte faux (F), valeur = produit répété (V)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    valeur_qcm_unique: genQcmSur(SIMPLES),
    sens_tableau_vf: genVfSur(SIMPLES),
    dix_flash_texte_trou_saisie: genFlashDix,
    dix_valeur_qcm_unique: genQcmSur(DIX),
    dix_sens_tableau_vf: genVfSur(DIX),
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'valeur', label: L_QCM, formatsList: [{ key: 'valeur_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

/** Les cartes de 1104 : les mêmes, en base 10 (clés dix_*). */
export const CATEGORIES_DIX = [
    { id: 'dix_flash', label: L_FLASH, formatsList: [{ key: 'dix_flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'dix_valeur', label: L_QCM, formatsList: [{ key: 'dix_valeur_qcm_unique', label: 'QCM' }] },
    { id: 'dix_sens', label: L_VF, formatsList: [{ key: 'dix_sens_tableau_vf', label: 'Vrai/Faux' }] },
];

const CLES_1103 = Object.keys(GENERATORS).filter((k) => !k.startsWith('dix_'));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = CLES_1103.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(CLES_1103);
    } else key = randomChoice(CLES_1103);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
